import {MantineColor} from '@mantine/core';

import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {TASK_STATUS_ICONS} from 'processes/status/TaskStatusIcon';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {
  ReviewItemStatusLabel,
  REVIEW_STATUS_LABEL_TO_SUMMARY_KEY,
  ReviewSummaryStateKey,
  TaskStatusLabel,
  TASK_STATUS_LABEL_TO_SUMMARY_KEY,
  TASK_STATUS_TYPE_TO_SUMMARY_KEY,
  TasksReviewSummaryCountKey,
} from './MyWorkConstants';

interface StatusMetadata<StatusLabel extends string, SummaryKey extends string> {
  color: MantineColor;
  icon: BenchlingIconName;
  label: StatusLabel;
  summaryKey: SummaryKey;
}

export type ReviewStatusMetadata = StatusMetadata<ReviewItemStatusLabel, ReviewSummaryStateKey>;

export interface TaskStatusMetadata extends StatusMetadata<TaskStatusLabel, TasksReviewSummaryCountKey> {
  statusType: ProcessTaskStatusType;
}

export const REVIEW_STATUS_METADATA: Record<ReviewItemStatusLabel, ReviewStatusMetadata> = {
  'in progress': {
    color: 'blue',
    icon: 'timer-outline',
    label: 'in progress',
    summaryKey: REVIEW_STATUS_LABEL_TO_SUMMARY_KEY['in progress'],
  },
  'needs revision': {
    color: 'red',
    icon: 'file-pen-outline',
    label: 'needs revision',
    summaryKey: REVIEW_STATUS_LABEL_TO_SUMMARY_KEY['needs revision'],
  },
  'ready for review': {
    color: 'yellow',
    icon: 'clipboard-list-outline',
    label: 'ready for review',
    summaryKey: REVIEW_STATUS_LABEL_TO_SUMMARY_KEY['ready for review'],
  },
  'sent for review': {
    color: 'gray',
    icon: 'paper-plane-top',
    label: 'sent for review',
    summaryKey: REVIEW_STATUS_LABEL_TO_SUMMARY_KEY['sent for review'],
  },
  completed: {
    color: 'green',
    icon: 'check-circle-outline',
    label: 'completed',
    summaryKey: REVIEW_STATUS_LABEL_TO_SUMMARY_KEY.completed,
  },
};

export const TASK_STATUS_METADATA: Record<TaskStatusLabel, TaskStatusMetadata> = {
  pending: {
    color: 'gray',
    icon: TASK_STATUS_ICONS[ProcessTaskStatusType.PENDING].icon,
    label: 'pending',
    statusType: ProcessTaskStatusType.PENDING,
    summaryKey: TASK_STATUS_LABEL_TO_SUMMARY_KEY.pending,
  },
  'in progress': {
    color: 'blue',
    icon: TASK_STATUS_ICONS[ProcessTaskStatusType.IN_PROGRESS].icon,
    label: 'in progress',
    statusType: ProcessTaskStatusType.IN_PROGRESS,
    summaryKey: TASK_STATUS_LABEL_TO_SUMMARY_KEY['in progress'],
  },
  failed: {
    color: 'red',
    icon: TASK_STATUS_ICONS[ProcessTaskStatusType.FAILED].icon,
    label: 'failed',
    statusType: ProcessTaskStatusType.FAILED,
    summaryKey: TASK_STATUS_LABEL_TO_SUMMARY_KEY.failed,
  },
  cancelled: {
    color: 'red',
    icon: TASK_STATUS_ICONS[ProcessTaskStatusType.CANCELLED].icon,
    label: 'cancelled',
    statusType: ProcessTaskStatusType.CANCELLED,
    summaryKey: TASK_STATUS_LABEL_TO_SUMMARY_KEY.cancelled,
  },
  invalid: {
    color: 'orange',
    icon: TASK_STATUS_ICONS[ProcessTaskStatusType.INVALID].icon,
    label: 'invalid',
    statusType: ProcessTaskStatusType.INVALID,
    summaryKey: TASK_STATUS_LABEL_TO_SUMMARY_KEY.invalid,
  },
  completed: {
    color: 'green',
    icon: TASK_STATUS_ICONS[ProcessTaskStatusType.COMPLETED].icon,
    label: 'completed',
    statusType: ProcessTaskStatusType.COMPLETED,
    summaryKey: TASK_STATUS_LABEL_TO_SUMMARY_KEY.completed,
  },
  skipped: {
    color: 'grape',
    icon: TASK_STATUS_ICONS[ProcessTaskStatusType.SKIPPED].icon,
    label: 'skipped',
    statusType: ProcessTaskStatusType.SKIPPED,
    summaryKey: TASK_STATUS_LABEL_TO_SUMMARY_KEY.skipped,
  },
};

export function getTaskStatusMetadataByType(
  statusType: ProcessTaskStatusType
): TaskStatusMetadata | undefined {
  const summaryKey = TASK_STATUS_TYPE_TO_SUMMARY_KEY[statusType];
  return Object.values(TASK_STATUS_METADATA).find((metadata) => metadata.summaryKey === summaryKey);
}
